// =============================================================================
// Skill-package DRAFT — editor state, wire payload and client-side caps (#3074)
//
// Until now the only way into the package format was a zip built outside the
// product. This file is the other half of the portal path: what an admin types
// into the editor, how it becomes a create/update body, and which of the
// backend's caps it would violate — checked HERE so the editor can show a
// budget running out instead of the server refusing after the fact.
//
// Two rules shape the conversions:
//
// * The backend model is `extra="forbid"` down to the eval expectation, so the
//   payload carries only fields the model knows. Reference/script maps are
//   dicts on the wire and ordered lists in the editor (a React list needs
//   stable keys and an admin needs to rename a path without losing focus).
// * Scripts are PRESERVED, never edited. A zip-imported package can carry
//   `scripts/` the portal has no editor for; round-tripping them untouched is
//   what keeps "edit an imported package in the portal" from silently
//   deleting half of it.
// =============================================================================

import type {
  SkillPackageDraft,
  SkillPackageDraftIdentity,
  SkillPackageSummary,
} from "@/api/admin/skill-packages"
import type { HostedAgentEvalCase } from "@/api/admin-api"

import {
  MAX_SKILL_PACKAGE_DELIVERY_CONTRACT_BYTES,
  MAX_SKILL_PACKAGE_DESCRIPTION_LENGTH,
  MAX_SKILL_PACKAGE_DISPLAY_NAME_LENGTH,
  MAX_SKILL_PACKAGE_EVAL_CASES,
  MAX_SKILL_PACKAGE_FILE_BYTES,
  MAX_SKILL_PACKAGE_FILES,
  MAX_SKILL_PACKAGE_GUIDANCE_EXCERPT_BYTES,
  MAX_SKILL_PACKAGE_INSTRUCTIONS_BYTES,
  MAX_SKILL_PACKAGE_NAME_LENGTH,
  MAX_SKILL_PACKAGE_REQUIRED_TOOLS,
  MAX_SKILL_PACKAGE_TOOL_RECOMMENDATIONS,
  MAX_SKILL_PACKAGE_TOTAL_BYTES,
  SKILL_PACKAGE_NAME_PATTERN,
  SKILL_PACKAGE_REFERENCE_SUFFIXES,
} from "@/api/admin/skill-packages"

import {
  createBlankHostedAgentEvalCase,
  type HostedAgentEvalCaseDraft,
  toHostedAgentEvalCaseDraft,
  toHostedAgentEvalCaseInput,
  validateHostedAgentEvalCase,
} from "./hosted-agent-eval-cases"

// ---------------------------------------------------------------------------
// Editor state
// ---------------------------------------------------------------------------

export type SkillPackageFileDraft = {
  /** Local-only React list key; never sent. */
  key: string
  path: string
  content: string
}

export type SkillPackageFormState = {
  /** Server id, or null while the package has never been saved. */
  id: string | null
  /** The revision the form was loaded at — the optimistic-concurrency token. */
  revision: number
  /** 0 while nothing is published; the frozen version number otherwise. */
  latestVersion: number
  name: string
  displayName: string
  description: string
  instructions: string
  deliveryContract: string
  guidanceExcerpt: string
  references: SkillPackageFileDraft[]
  /** Carried through untouched — the portal has no script editor. */
  scripts: Record<string, string>
  recommendedTools: string[]
  requiredTools: string[]
  evalCases: HostedAgentEvalCaseDraft[]
}

let fileKeySeed = 0

function nextFileKey(): string {
  fileKeySeed += 1
  return `skill-package-file-${fileKeySeed}`
}

export function createBlankSkillPackageForm(): SkillPackageFormState {
  return {
    id: null,
    revision: 0,
    latestVersion: 0,
    name: "",
    displayName: "",
    description: "",
    instructions: "",
    deliveryContract: "",
    guidanceExcerpt: "",
    references: [],
    scripts: {},
    recommendedTools: [],
    requiredTools: [],
    evalCases: [],
  }
}

export function createBlankSkillPackageFile(): SkillPackageFileDraft {
  return { key: nextFileKey(), path: "", content: "" }
}

function filesFromRecord(record: Record<string, string> | undefined): SkillPackageFileDraft[] {
  return Object.entries(record ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([path, content]) => ({ key: nextFileKey(), path, content }))
}

/**
 * A package eval case as the shared case editor's draft.
 *
 * Package cases have no server id (they are seeded onto an agent at install
 * time, and only then become identified objects), so a synthetic id keeps the
 * editor's per-case input ids unique without ever being sent.
 */
function evalCaseToDraft(source: Record<string, unknown>, index: number): HostedAgentEvalCaseDraft {
  const draft = toHostedAgentEvalCaseDraft({
    id: `package-case-${index}`,
    name: String(source.name ?? ""),
    prompt: String(source.prompt ?? ""),
    enabled: source.enabled !== false,
    timeout_seconds: Number(source.timeout_seconds ?? 120),
    expectations: Array.isArray(source.expectations) ? source.expectations : [],
  } as unknown as HostedAgentEvalCase)
  // Package cases are positional, not identified: null keeps "save" from ever
  // reading this as an existing server object.
  return { ...draft, id: `package-case-${index}` }
}

export function toSkillPackageForm(pkg: SkillPackageSummary): SkillPackageFormState {
  const content = pkg.draft.content
  return {
    id: pkg.id,
    revision: pkg.revision,
    latestVersion: pkg.latest_version,
    name: pkg.name,
    displayName: pkg.display_name || pkg.name,
    description: pkg.description || content.description,
    instructions: content.instructions,
    deliveryContract: content.delivery_contract,
    guidanceExcerpt: content.guidance_excerpt,
    references: filesFromRecord(content.references),
    scripts: { ...content.scripts },
    recommendedTools: [...pkg.draft.tool_recommendations.tools],
    requiredTools: [...pkg.draft.required_tools],
    evalCases: pkg.draft.eval_cases.map(evalCaseToDraft),
  }
}

/**
 * A generated draft into editor state, keeping the form's identity.
 *
 * The assistant fills an EXISTING form rather than replacing it: an admin who
 * already created the package (so it has an id and a revision) must not lose
 * that by asking for help, or the next save would try to create a duplicate.
 */
export function applyGeneratedDraft(
  form: SkillPackageFormState,
  generated: SkillPackageDraft & SkillPackageDraftIdentity
): SkillPackageFormState {
  const content = generated.content
  return {
    ...form,
    name: form.name.trim() || generated.name,
    displayName: generated.display_name || generated.name,
    description: generated.description || content.description,
    instructions: content.instructions,
    deliveryContract: content.delivery_contract,
    guidanceExcerpt: content.guidance_excerpt,
    references: filesFromRecord(content.references),
    // Scripts stay whatever the form already had: the assistant never writes
    // executable content, and an imported package's scripts must survive.
    recommendedTools: [...generated.tool_recommendations.tools],
    requiredTools: [...generated.required_tools],
    evalCases: generated.eval_cases.map(evalCaseToDraft),
  }
}

// ---------------------------------------------------------------------------
// Wire payload
// ---------------------------------------------------------------------------

function recordFromFiles(files: SkillPackageFileDraft[]): Record<string, string> {
  const record: Record<string, string> = {}
  for (const file of files) {
    const path = file.path.trim()
    if (path) record[path] = file.content
  }
  return record
}

/**
 * Form → create/update body.
 *
 * `revision` is included only when the package exists: the backend requires it
 * on PUT (optimistic concurrency) and forbids it on POST.
 */
export function toSkillPackagePayload(
  form: SkillPackageFormState
): Record<string, unknown> & { revision?: number } {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    display_name: form.displayName.trim() || form.name.trim(),
    description: form.description.trim(),
    content: {
      description: form.description.trim(),
      instructions: form.instructions,
      references: recordFromFiles(form.references),
      scripts: { ...form.scripts },
      delivery_contract: form.deliveryContract,
      guidance_excerpt: form.guidanceExcerpt,
    },
    tool_recommendations: { tools: [...form.recommendedTools], tool_actions: {} },
    required_tools: [...form.requiredTools],
    eval_cases: form.evalCases.map((draft) => toHostedAgentEvalCaseInput(draft)),
  }
  if (form.id) payload.revision = form.revision
  return payload
}

// ---------------------------------------------------------------------------
// Budgets — the caps, measured the way the server measures them
// ---------------------------------------------------------------------------

const encoder = new TextEncoder()

/** UTF-8 byte length. The server caps BYTES; `"ö".length` is 1 and its cost is 2. */
export function byteLength(text: string): number {
  return encoder.encode(text).length
}

export type SkillPackageBudget = { used: number; max: number; over: boolean }

function budget(used: number, max: number): SkillPackageBudget {
  return { used, max, over: used > max }
}

export type SkillPackageBudgets = {
  instructions: SkillPackageBudget
  deliveryContract: SkillPackageBudget
  guidanceExcerpt: SkillPackageBudget
  description: SkillPackageBudget
  files: SkillPackageBudget
  total: SkillPackageBudget
  evalCases: SkillPackageBudget
  recommendedTools: SkillPackageBudget
  requiredTools: SkillPackageBudget
}

/** Every cap the server would apply, as live counters for the editor. */
export function skillPackageBudgets(form: SkillPackageFormState): SkillPackageBudgets {
  const instructions = byteLength(form.instructions)
  const referenceBytes = form.references.reduce((sum, file) => sum + byteLength(file.content), 0)
  const scriptBytes = Object.values(form.scripts).reduce((sum, text) => sum + byteLength(text), 0)
  return {
    instructions: budget(instructions, MAX_SKILL_PACKAGE_INSTRUCTIONS_BYTES),
    deliveryContract: budget(
      byteLength(form.deliveryContract),
      MAX_SKILL_PACKAGE_DELIVERY_CONTRACT_BYTES
    ),
    guidanceExcerpt: budget(
      byteLength(form.guidanceExcerpt),
      MAX_SKILL_PACKAGE_GUIDANCE_EXCERPT_BYTES
    ),
    description: budget(form.description.length, MAX_SKILL_PACKAGE_DESCRIPTION_LENGTH),
    files: budget(
      form.references.length + Object.keys(form.scripts).length,
      MAX_SKILL_PACKAGE_FILES
    ),
    total: budget(instructions + referenceBytes + scriptBytes, MAX_SKILL_PACKAGE_TOTAL_BYTES),
    evalCases: budget(form.evalCases.length, MAX_SKILL_PACKAGE_EVAL_CASES),
    recommendedTools: budget(form.recommendedTools.length, MAX_SKILL_PACKAGE_TOOL_RECOMMENDATIONS),
    requiredTools: budget(form.requiredTools.length, MAX_SKILL_PACKAGE_REQUIRED_TOOLS),
  }
}

// ---------------------------------------------------------------------------
// Validation, mirroring the pydantic model
// ---------------------------------------------------------------------------

export type SkillPackageFormErrorCode =
  | "name_required"
  | "name_not_a_slug"
  | "name_too_long"
  | "display_name_too_long"
  | "description_too_long"
  | "instructions_required"
  | "instructions_too_long"
  | "delivery_contract_too_long"
  | "guidance_excerpt_too_long"
  | "reference_path_required"
  | "reference_path_invalid"
  | "reference_path_duplicate"
  | "reference_suffix_invalid"
  | "reference_too_large"
  | "too_many_files"
  | "total_too_large"
  | "too_many_eval_cases"
  | "eval_case_invalid"
  | "eval_case_duplicate"
  | "too_many_recommended_tools"
  | "too_many_required_tools"

export type SkillPackageFormValidation = {
  form: SkillPackageFormErrorCode[]
  /**
   * Keyed by the reference draft's local `key`. `Partial` on purpose: a row
   * with no error has no entry, and the editor must be able to ask "does this
   * row have one?" without the type claiming every key resolves.
   */
  references: Partial<Record<string, SkillPackageFormErrorCode>>
  /** Keyed by the eval case draft's local `id`; `Partial` for the same reason. */
  evalCases: Partial<Record<string, "eval_case_invalid">>
}

/** `_rel_path_ok` plus the reference-specific rules, in the browser. */
function referencePathError(path: string): SkillPackageFormErrorCode | null {
  const trimmed = path.trim()
  if (!trimmed) return "reference_path_required"
  if (
    trimmed.length > 200 ||
    trimmed.includes("\\") ||
    trimmed.startsWith("/") ||
    trimmed.endsWith("/") ||
    trimmed.split("/").some((part) => part === "" || part === "." || part === "..") ||
    !/^[A-Za-z0-9._][A-Za-z0-9._/-]*$/.test(trimmed) ||
    trimmed.split("/")[0] === "scripts"
  ) {
    return "reference_path_invalid"
  }
  const lower = trimmed.toLowerCase()
  if (!SKILL_PACKAGE_REFERENCE_SUFFIXES.some((suffix) => lower.endsWith(suffix))) {
    return "reference_suffix_invalid"
  }
  return null
}

export function validateSkillPackageForm(form: SkillPackageFormState): SkillPackageFormValidation {
  const errors: SkillPackageFormErrorCode[] = []
  const references: Record<string, SkillPackageFormErrorCode> = {}
  const evalCases: Record<string, "eval_case_invalid"> = {}
  const budgets = skillPackageBudgets(form)

  const name = form.name.trim()
  if (!name) errors.push("name_required")
  else if (name.length > MAX_SKILL_PACKAGE_NAME_LENGTH) errors.push("name_too_long")
  else if (!SKILL_PACKAGE_NAME_PATTERN.test(name)) errors.push("name_not_a_slug")

  if (form.displayName.trim().length > MAX_SKILL_PACKAGE_DISPLAY_NAME_LENGTH) {
    errors.push("display_name_too_long")
  }
  if (budgets.description.over) errors.push("description_too_long")

  if (!form.instructions.trim()) errors.push("instructions_required")
  else if (budgets.instructions.over) errors.push("instructions_too_long")
  if (budgets.deliveryContract.over) errors.push("delivery_contract_too_long")
  if (budgets.guidanceExcerpt.over) errors.push("guidance_excerpt_too_long")

  const seenPaths = new Set<string>()
  for (const file of form.references) {
    const pathError = referencePathError(file.path)
    if (pathError) {
      references[file.key] = pathError
      continue
    }
    const path = file.path.trim()
    if (seenPaths.has(path)) {
      references[file.key] = "reference_path_duplicate"
      continue
    }
    seenPaths.add(path)
    if (byteLength(file.content) > MAX_SKILL_PACKAGE_FILE_BYTES) {
      references[file.key] = "reference_too_large"
    }
  }
  if (Object.keys(references).length > 0) {
    const first = Object.values(references)[0]
    if (!errors.includes(first)) errors.push(first)
  }
  if (budgets.files.over) errors.push("too_many_files")
  if (budgets.total.over) errors.push("total_too_large")

  if (budgets.evalCases.over) errors.push("too_many_eval_cases")
  const seenCaseNames = new Set<string>()
  for (const evalCase of form.evalCases) {
    const key = evalCase.id ?? evalCase.name
    if (validateHostedAgentEvalCase(evalCase).case.length > 0) {
      evalCases[key] = "eval_case_invalid"
      continue
    }
    const caseName = evalCase.name.trim()
    if (seenCaseNames.has(caseName)) evalCases[key] = "eval_case_invalid"
    seenCaseNames.add(caseName)
  }
  if (Object.keys(evalCases).length > 0) errors.push("eval_case_invalid")

  if (budgets.recommendedTools.over) errors.push("too_many_recommended_tools")
  if (budgets.requiredTools.over) errors.push("too_many_required_tools")

  return { form: errors, references, evalCases }
}

export function isSkillPackageFormValid(form: SkillPackageFormState): boolean {
  return validateSkillPackageForm(form).form.length === 0
}

/** A new, empty eval case for the package editor. */
export function createBlankSkillPackageEvalCase(index: number): HostedAgentEvalCaseDraft {
  return { ...createBlankHostedAgentEvalCase(), id: `package-case-new-${index}` }
}

/** `"Anbud Analys"` → `"anbud-analys"`: the slug the server would demand. */
export function slugifySkillPackageName(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SKILL_PACKAGE_NAME_LENGTH)
}
